import { InputAdornment, Stack } from "@mui/material";
import { Dayjs } from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { resolveUsersDateRange } from "../../../../../Users/usersDateDefaults";

interface UsersDateFilterProps {
  dateRange: [Dayjs | null, Dayjs | null];
  onDateRangeChange: (range: [Dayjs | null, Dayjs | null]) => void;
}

// `color="secondary"` on the text field slot doesn't reach the calendar
// popup itself - its selected-day highlight, month navigation arrows, and
// month/year view-switch icon all default to the theme's primary (MUI
// default blue) independently of the field, same for the Cancel/OK action
// bar's buttons (checklist Section 0). This targets all of them via the
// mobile popup's `dialog` slot (the calendar renders as a Dialog, not a
// Popper, once Cancel/OK actions are present).
const BRAND = "#8E24AA";
const calendarDialogSx = {
  "& .MuiPickersDay-root.Mui-selected": {
    backgroundColor: BRAND,
    "&:hover, &:focus": { backgroundColor: "#6d1b85" },
  },
  "& .MuiPickersArrowSwitcher-button, & .MuiPickersCalendarHeader-switchViewButton":
    {
      color: BRAND,
    },
  "& .MuiDialogActions-root .MuiButton-root": {
    color: BRAND,
  },
};

// A purely decorative icon, not an "open picker" button - unlike the
// desktop picker (useDesktopPicker.js), the mobile picker variant
// (useMobilePicker.js) has no `openPickerButton`/`openPickerIcon` concept at
// all: on mobile the entire field is the tap target (it wires `onClick:
// actions.onOpen` straight onto the field and defaults it `readOnly`), so
// `slots.openPickerIcon` - the first attempt - silently did nothing, since
// that slot is only ever read by the desktop rendering path. This is added
// via the text field's own `InputProps.endAdornment` instead, which renders
// the same way regardless of which picker variant is active.
// `pointerEvents: "none"` so a tap landing on the icon itself still bubbles
// up to the field's own click-to-open handler rather than being absorbed by
// the icon.
const calendarAdornment = (
  <InputAdornment position="end">
    <CalendarMonthIcon sx={{ fontSize: 20, pointerEvents: "none" }} />
  </InputAdornment>
);

// Two SEPARATE Start/End date pickers, unlike the desktop UsersFilterItems.tsx
// (a single combined CustomDateRangePicker) - the mobile mockup shows two
// distinct fields, and checklist Section 8 point 10's rule ("mockup shows
// two separate inputs -> use two separate DatePicker components") applies
// even though the mockup itself renders them as raw <input type="date">
// (a prototyping-tool limitation, not a literal spec - see
// docs/module-configs/optifund-dashboard-mobile.jsonc caveat 1).
//
// Wired to `onAccept`, not `onChange`: MUI's DatePicker calls `onChange`
// live as the user taps around the calendar, before confirming - wiring
// that straight to onDateRangeChange would refetch on every tap instead of
// only once the user actually presses OK. `onAccept` only fires when OK is
// pressed (Cancel reverts without calling either), which is the real commit
// point queries should react to.
//
// Deliberately does NOT use the shared readOnlyDateInputProps/
// readOnlyDateInputSx (datePickerReadOnlyInput.ts) that the desktop pickers
// use - that block's onFocus:blur() trick is built for the desktop mouse/
// focus model. On mobile it's redundant (useMobilePicker already defaults
// the field to readOnly and opens the calendar via onClick, not focus) and
// actively caused a bug: the field's outline got stuck permanently
// highlighted, most likely a race between the forced blur and the dialog
// restoring focus to the field on close. Without it, the field's outline
// just follows real native focus state, which behaves correctly here.
export default function UsersDateFilter({
  dateRange,
  onDateRangeChange,
}: UsersDateFilterProps) {
  const [startDate, endDate] = resolveUsersDateRange(dateRange);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack direction="row" spacing={1.5}>
        <DatePicker
          label="Start date"
          value={startDate}
          maxDate={endDate}
          format="DD-MM-YYYY"
          enableAccessibleFieldDOMStructure={false}
          onAccept={(value) => onDateRangeChange([value, endDate])}
          sx={{ flex: 1 }}
          slotProps={{
            textField: {
              size: "small",
              color: "secondary",
              fullWidth: true,
              InputProps: { endAdornment: calendarAdornment },
            } as any,
            dialog: { sx: calendarDialogSx },
          }}
        />
        <DatePicker
          label="End date"
          value={endDate}
          minDate={startDate}
          format="DD-MM-YYYY"
          enableAccessibleFieldDOMStructure={false}
          onAccept={(value) => onDateRangeChange([startDate, value])}
          sx={{ flex: 1 }}
          slotProps={{
            textField: {
              size: "small",
              color: "secondary",
              fullWidth: true,
              InputProps: { endAdornment: calendarAdornment },
            } as any,
            dialog: { sx: calendarDialogSx },
          }}
        />
      </Stack>
    </LocalizationProvider>
  );
}
